/**
 * V10 Phase C — Timeline Playback 상단 재생바.
 *
 * - slider (input range) + Play/Pause/First/Latest 버튼
 * - Speed dropdown (1d/s · 1w/s · 1mo/s)
 * - Loop 체크박스
 * - asOf datetime-local 선택
 *
 * 재생 중 자체적으로 asOf 를 advance — useAsOf 의 setAsOf 를 호출.
 * `prefers-reduced-motion` 은 재생 애니메이션 여부에 영향 없음 (값 업데이트는 항상 유지).
 */
import { useEffect, useMemo, useState } from "react"
import { useAsOf } from "../../../hooks/useAsOf.tsx"
import { TimelineHeatmap } from "./TimelineHeatmap.tsx"

/** 서버 fetch 를 트리거할 asOf 커밋 간격. 초당 1회 (rate-limit 친화 + graph 쿼리 debounce 200ms 와 평형).
 *  UI 표시는 RAF 로 매 프레임 보간해 부드럽게 흐르게 한다 — `displayMs` 상태 참조. */
const TICK_INTERVAL_MS = 1000

type Speed = "day" | "week" | "month"

type Props = {
	readonly range:
		| {
				readonly min: string
				readonly max: string
		  }
		| undefined
	readonly buckets: ReadonlyArray<{ readonly bucket: string; readonly count: number }>
}

const SPEED_MS_PER_SECOND: Record<Speed, number> = {
	day: 24 * 3600 * 1000,
	week: 7 * 24 * 3600 * 1000,
	month: 30 * 24 * 3600 * 1000
}

export function TimelineBar(props: Props) {
	const { asOf, setAsOf } = useAsOf()
	const [speed, setSpeed] = useState<Speed>("week")
	const [loop, setLoop] = useState(false)
	const [playing, setPlaying] = useState(false)

	const minMs = props.range ? Date.parse(props.range.min) : Number.NaN
	const maxMs = props.range ? Date.parse(props.range.max) : Number.NaN
	const hasRange = Number.isFinite(minMs) && Number.isFinite(maxMs)
	const asOfMs = asOf ? Date.parse(asOf) : hasRange ? maxMs : Date.now()

	// 화면 표시용 시각 (RAF 보간). 재생 정지/슬라이더 조작 시 asOfMs 와 동기화.
	const [displayMs, setDisplayMs] = useState<number>(asOfMs)
	useEffect(() => {
		if (!playing) setDisplayMs(asOfMs)
	}, [asOfMs, playing])

	const sliderValue = useMemo(() => {
		if (!hasRange) return 0
		const span = Math.max(1, maxMs - minMs)
		return Math.max(0, Math.min(1000, Math.round(((displayMs - minMs) / span) * 1000)))
	}, [displayMs, minMs, maxMs, hasRange])

	// 재생 루프 — RAF 로 매 프레임 displayMs 를 보간 흘리고, TICK_INTERVAL_MS 마다 setAsOf 커밋해
	// 서버 fetch 를 트리거. 서버 호출 빈도는 초당 1회로 유지, 화면은 60fps 로 부드러움.
	// biome-ignore lint/correctness/useExhaustiveDependencies: asOfMs 는 루프 시작 앵커로만 쓰여 의도적 제외 — 루프 내부가 asOfMs 를 자체 전진시키므로 dep 추가 시 tick 마다 effect 재시작되어 drift
	useEffect(() => {
		if (!playing || !hasRange) return
		const deltaPerSec = SPEED_MS_PER_SECOND[speed]
		let raf = 0
		let anchorPerf = performance.now()
		let anchorAsOf = asOfMs
		let stopped = false

		const step = () => {
			if (stopped) return
			const now = performance.now()
			const elapsed = now - anchorPerf
			// 보간 표시
			const projected = anchorAsOf + (deltaPerSec * elapsed) / TICK_INTERVAL_MS
			setDisplayMs(Math.min(maxMs, Math.max(minMs, projected)))
			// 1초 경과 → 서버에 커밋
			if (elapsed >= TICK_INTERVAL_MS) {
				const nextAsOf = anchorAsOf + deltaPerSec
				if (nextAsOf > maxMs) {
					if (loop) {
						anchorAsOf = minMs
						anchorPerf = now
						setAsOf(new Date(minMs).toISOString())
					} else {
						setAsOf(new Date(maxMs).toISOString())
						setPlaying(false)
						return
					}
				} else {
					anchorAsOf = nextAsOf
					anchorPerf = now
					setAsOf(new Date(nextAsOf).toISOString())
				}
			}
			raf = requestAnimationFrame(step)
		}
		raf = requestAnimationFrame(step)
		return () => {
			stopped = true
			cancelAnimationFrame(raf)
		}
	}, [playing, speed, hasRange, maxMs, minMs, loop, setAsOf])

	function onSliderChange(v: number) {
		if (!hasRange) return
		const span = maxMs - minMs
		const ms = minMs + (v / 1000) * span
		setAsOf(new Date(ms).toISOString())
	}

	function onDateTimeChange(v: string) {
		// datetime-local 은 초 단위 — ISO 로 변환
		if (!v) {
			setAsOf(undefined)
			return
		}
		const ms = Date.parse(v)
		if (!Number.isFinite(ms)) return
		setAsOf(new Date(ms).toISOString())
	}

	const dtLocal = hasRange ? new Date(displayMs).toISOString().slice(0, 16) : ""

	return (
		<div
			style={{
				padding: "var(--pk-space-3)",
				borderBottom: "1px solid var(--pk-border)",
				background: "var(--pk-bg-elevated)",
				display: "flex",
				flexDirection: "column",
				gap: "var(--pk-space-2)"
			}}
		>
			<div
				style={{
					display: "flex",
					alignItems: "center",
					gap: "var(--pk-space-2)",
					marginBottom: 2
				}}
			>
				<span style={{ fontSize: "0.8rem", fontWeight: 600, color: "var(--pk-text-primary)" }}>
					⏱ Timeline Playback
				</span>
				<span style={{ fontSize: "0.7rem", color: "var(--pk-text-muted)" }}>
					과거 시점의 지식 그래프를 재생해 변화를 확인합니다. 재생하면 asOf 가 자동으로 흐릅니다.
				</span>
			</div>
			<div
				style={{
					display: "flex",
					alignItems: "center",
					gap: "var(--pk-space-2)",
					flexWrap: "wrap"
				}}
			>
				<button
					type="button"
					className="btn btn-ghost btn-sm"
					onClick={() => hasRange && setAsOf(new Date(minMs).toISOString())}
					disabled={!hasRange}
					title="처음 시점(가장 오래된 episode)으로 이동"
					aria-label="처음으로"
				>
					⏮
				</button>
				<button
					type="button"
					className="btn btn-primary btn-sm"
					onClick={() => setPlaying(!playing)}
					disabled={!hasRange}
					title={playing ? "재생 일시정지" : "현재 시점부터 asOf 를 자동으로 흐르게 재생"}
					aria-label={playing ? "일시정지" : "재생"}
				>
					{playing ? "⏸" : "▶"}
				</button>
				<button
					type="button"
					className="btn btn-ghost btn-sm"
					onClick={() => hasRange && setAsOf(new Date(maxMs).toISOString())}
					disabled={!hasRange}
					title="최신 시점(가장 최근 episode)으로 이동"
					aria-label="최신으로"
				>
					⏭
				</button>

				<select
					className="input"
					value={speed}
					onChange={(e) => setSpeed(e.target.value as Speed)}
					aria-label="재생 속도"
					title="재생 속도 — 재생 1초당 asOf 가 얼마만큼 흐를지"
					style={{ fontSize: "0.8rem", padding: "4px 8px" }}
				>
					<option value="day">1일/초</option>
					<option value="week">1주/초</option>
					<option value="month">1달/초</option>
				</select>

				<label
					style={{ fontSize: "0.8rem", display: "flex", alignItems: "center", gap: 4 }}
					title="끝에 도달하면 처음으로 되돌아가 반복 재생"
				>
					<input type="checkbox" checked={loop} onChange={(e) => setLoop(e.target.checked)} />
					Loop
				</label>

				<input
					className="input"
					type="datetime-local"
					value={dtLocal}
					onChange={(e) => onDateTimeChange(e.target.value)}
					disabled={!hasRange}
					lang="ko-KR"
					title="특정 시점을 직접 입력. 이 시점 기준의 그래프/엣지가 표시됩니다."
					aria-label="asOf 시점 선택"
					style={{ fontSize: "0.8rem", padding: "4px 8px", marginLeft: "auto" }}
				/>
			</div>

			{!hasRange ? (
				<p
					style={{
						margin: 0,
						fontSize: "0.75rem",
						color: "var(--pk-text-muted)",
						fontStyle: "italic"
					}}
				>
					이 서비스의 episode 가 없어 타임라인을 표시할 수 없습니다
				</p>
			) : (
				<div>
					<input
						type="range"
						min={0}
						max={1000}
						value={sliderValue}
						onChange={(e) => onSliderChange(Number(e.target.value))}
						disabled={!hasRange}
						aria-label="타임라인 슬라이더"
						title="드래그해서 asOf 시점을 이동"
						style={{
							width: "100%",
							margin: 0,
							padding: 0,
							background: "transparent"
						}}
					/>
					<div
						style={{
							display: "flex",
							justifyContent: "space-between",
							fontSize: "0.65rem",
							color: "var(--pk-text-muted)",
							fontFamily: "var(--pk-font-mono)"
						}}
					>
						<span title={new Date(minMs).toISOString()}>
							{new Date(minMs).toLocaleDateString("ko-KR", {
								year: "numeric",
								month: "2-digit",
								day: "2-digit"
							})}
						</span>
						<span>
							{asOf || playing
								? `asOf: ${new Date(displayMs).toLocaleString("ko-KR", {
										year: "numeric",
										month: "2-digit",
										day: "2-digit",
										hour: "2-digit",
										minute: "2-digit",
										second: "2-digit"
									})}`
								: `${new Date(minMs).toLocaleDateString("ko-KR")} ~ ${new Date(maxMs).toLocaleDateString("ko-KR")}`}
						</span>
						<span title={new Date(maxMs).toISOString()}>
							{new Date(maxMs).toLocaleDateString("ko-KR", {
								year: "numeric",
								month: "2-digit",
								day: "2-digit"
							})}
						</span>
					</div>
				</div>
			)}

			{hasRange && <TimelineHeatmap buckets={props.buckets} />}
		</div>
	)
}
